import { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";

/**
 * Theme system — supports three user-selectable modes:
 *   • "auto"  — follows the device's `prefers-color-scheme` (default)
 *   • "light" — force light
 *   • "dark"  — force dark
 *
 * `theme` (consumer-visible) is the RESOLVED value ("light" | "dark")
 * `mode` (consumer-visible)  is the user's CHOICE ("auto" | "light" | "dark")
 *
 * Iter 231.8 — Default is "auto" so first-time visitors get the same
 * theme as the rest of their device. Anyone who used the app before
 * Iter 231.8 had "dark" persisted, and stays on dark (we don't yank
 * existing preferences out from under them).
 */
const ThemeContext = createContext({
  theme: "dark",
  mode: "auto",
  setMode: () => {},
  setTheme: () => {},
  toggle: () => {},
});

const KEY = "hv_theme";

function _systemPrefersDark() {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function _resolve(mode) {
  if (mode === "light" || mode === "dark") return mode;
  return _systemPrefersDark() ? "dark" : "light";
}

function applyTheme(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", theme === "light" ? "#f5f7fa" : "#0a0b0e");
  }
}

// Run as early as possible so we don't see a dark→light flash.
// Returns the resolved theme ("light" | "dark").
export function bootstrapTheme() {
  if (typeof window === "undefined") return "dark";
  const saved = localStorage.getItem(KEY);
  // Backwards compat — anything other than "auto"/"light"/"dark"
  // is treated as "auto" so new visitors follow their system.
  const mode = (saved === "light" || saved === "dark" || saved === "auto") ? saved : "auto";
  const theme = _resolve(mode);
  applyTheme(theme);
  return theme;
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    if (typeof window === "undefined") return "auto";
    const saved = localStorage.getItem(KEY);
    return (saved === "light" || saved === "dark" || saved === "auto") ? saved : "auto";
  });
  const [theme, setThemeResolved] = useState(() => _resolve(mode));

  // Subscribe to the OS `prefers-color-scheme` change ONLY while we're in
  // "auto" mode. Cleans up the listener on mode change to avoid leaks.
  const mqlRef = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    mqlRef.current = mql;
    if (mode !== "auto") {
      const t = _resolve(mode);
      setThemeResolved(t);
      applyTheme(t);
      return undefined;
    }
    const t = _resolve("auto");
    setThemeResolved(t);
    applyTheme(t);
    const handler = (e) => {
      const next = e.matches ? "dark" : "light";
      setThemeResolved(next);
      applyTheme(next);
    };
    // Some Safari versions only support addListener
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else if (mql.addListener) mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else if (mql.removeListener) mql.removeListener(handler);
    };
  }, [mode]);

  const setMode = useCallback((m) => {
    if (m !== "auto" && m !== "light" && m !== "dark") return;
    setModeState(m);
    try { localStorage.setItem(KEY, m); } catch (_e) { /* private mode */ }
    const t = _resolve(m);
    setThemeResolved(t);
    applyTheme(t);
  }, []);

  // Back-compat shim — old callers used `setTheme("light"|"dark")`. Map
  // that to a hard mode set (light/dark force the resolved value).
  const setTheme = useCallback((t) => {
    if (t === "light" || t === "dark" || t === "auto") setMode(t);
  }, [setMode]);

  // Toggle cycles: auto → light → dark → auto. Most users only need
  // the 2-way toggle, but keeping the rotation lets advanced users
  // reach all 3 modes without opening Settings.
  const toggle = useCallback(() => {
    setMode(mode === "auto" ? "light" : mode === "light" ? "dark" : "auto");
  }, [mode, setMode]);

  // Keep tab-to-tab in sync.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== KEY) return;
      const next = (e.newValue === "light" || e.newValue === "dark" || e.newValue === "auto")
        ? e.newValue : "auto";
      setModeState(next);
      const t = _resolve(next);
      setThemeResolved(t);
      applyTheme(t);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(
    () => ({ theme, mode, setMode, setTheme, toggle }),
    [theme, mode, setMode, setTheme, toggle],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
